import type { CoachItemReach } from "@/lib/types";

/**
 * "Seen 2/3 · Ava: Need help" — did this piece of feedback land?
 *
 * A coach could see who they sent an item to, and separately a flat
 * list of responses, but never the two joined per item. Effort with no
 * visible echo is the classic reason a volunteer tool gets abandoned:
 * the coach concludes nobody looks at this, even while families read.
 *
 * COACH SURFACES ONLY. The data is the coach's own delivery picture —
 * a player must never learn how many other families opened an item, or
 * which of them asked for help. `tests/e2e/feedback-privacy.spec.js`
 * asserts this testid renders on no family tab; that assertion was
 * written before this component existed.
 *
 * Renders nothing for an unassigned item: "Seen 0/0" is noise, and a
 * team-wide note has no per-player denominator to report.
 */

const SIGNAL_LABELS: Record<string, string> = {
  need_help: "Need help",
  tried_this: "Tried this",
  got_it: "Got it",
};

export function ReachChip({ reach }: { reach: CoachItemReach | undefined }) {
  if (!reach || reach.assigned_count === 0) return null;

  const { reviewed_count: seen, assigned_count: assigned, responders } = reach;
  const allSeen = seen === assigned;
  // `need_help` is sorted first server-side, so the one the coach most
  // needs survives truncation to a single name.
  const lead = responders[0];

  return (
    <span
      className="flex shrink-0 items-center gap-1 text-ink-muted"
      data-testid="coach-reach-chip"
      data-reach-seen={seen}
      data-reach-assigned={assigned}
    >
      <span aria-hidden="true">·</span>
      <span className={allSeen ? "text-good" : undefined}>
        Seen {seen}/{assigned}
      </span>
      {lead ? (
        <>
          <span aria-hidden="true">·</span>
          <span className={lead.signal === "need_help" ? "text-flag" : undefined}>
            {lead.player_display_name}: {SIGNAL_LABELS[lead.signal] ?? lead.signal}
          </span>
          {responders.length > 1 ? <span>+{responders.length - 1}</span> : null}
        </>
      ) : null}
    </span>
  );
}
